/**
 * OAuth Redirect Page
 * 
 * Handles OAuth callbacks with two scenarios:
 * 1. Deep link worked (Safari): We're already in the app - exchange code directly
 * 2. Deep link failed (Chrome): We're in browser - write to DB for polling
 */

import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { detectNativeApp } from "@/lib/platform";
import { useNavigate } from "react-router-dom";

const OAuthRedirect = () => {
  const { t } = useTranslation();

  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [inNativeApp, setInNativeApp] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check if we're in the native app (deep link worked) or browser (deep link failed)
        const detection = await detectNativeApp();
        setInNativeApp(detection.isNative);

        // Get session ID and auth code from URL
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));

        const sessionId = urlParams.get('session_id'); // Our custom param
        const code = urlParams.get('code') || hashParams.get('code'); // PKCE auth code

        console.log('[OAuth Redirect] Received callback', {
          sessionId,
          hasCode: !!code,
          inNativeApp: detection.isNative
        });

        if (!code) {
          throw new Error('Missing authorization code');
        }

        // Determine the flow:
        // 1. In native app (Safari deep link worked) = normal flow, let Supabase handle it
        // 2. In browser with session_id (Chrome/popup polling) = write to DB
        // 3. In browser without session_id (desktop web) = normal flow
        // Note: Mobile Safari redirect flow now goes directly to /auth, not here

        if (detection.isNative) {
          // Native app (Safari deep link) - let Supabase auto-detect and exchange session
          console.log('[OAuth Redirect] In native app - letting Supabase handle session automatically');

          // Wait for Supabase to detect and exchange the session from URL
          // Check for session every 500ms for up to 5 seconds
          for (let i = 0; i < 10; i++) {
            await new Promise(resolve => setTimeout(resolve, 500));
            const { data: { session } } = await supabase.auth.getSession();

            if (session) {
              console.log('[OAuth Redirect] Session detected! Navigating to home');
              navigate('/', { replace: true });
              return;
            }
          }

          // If we get here, session detection failed
          throw new Error('Session detection timed out');
        } else if (sessionId) {
          // External browser with session_id (Chrome/popup polling flow) - write to DB
          console.log('[OAuth Redirect] External browser polling flow - writing to DB');
          const { error } = await supabase
            .from('oauth_sessions' as any)
            .update({
              auth_code: code,
              status: 'completed',
              completed_at: new Date().toISOString()
            } as any)
            .eq('session_id', sessionId);

          if (error) {
            console.error('[OAuth Redirect] Failed to update session:', error);
            throw error;
          }

          console.log('[OAuth Redirect] Successfully wrote auth code to session');
          setStatus('success');
        } else {
          // Desktop web without session_id - normal flow
          console.log('[OAuth Redirect] Desktop web - letting Supabase handle session automatically');

          // Wait for Supabase to detect and exchange the session from URL
          for (let i = 0; i < 10; i++) {
            await new Promise(resolve => setTimeout(resolve, 500));
            const { data: { session } } = await supabase.auth.getSession();

            if (session) {
              console.log('[OAuth Redirect] Session detected! Navigating to home');
              navigate('/', { replace: true });
              return;
            }
          }

          throw new Error('Session detection timed out');
        }
      } catch (error: any) {
        console.error('[OAuth Redirect] Error:', error);
        setErrorMessage(error.message || 'Failed to complete authentication');
        setStatus('error');
      }
    };

    handleCallback();
  }, []);

  const handleClose = () => {
    window.close();
  };

  // Render localized status UI

  // Success - show "open app" UI (polling case)
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="text-center space-y-6 max-w-md">
        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto bg-green-500/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold">{t('ui.text.sign_in_complete')}</h1>
              <p className="text-muted-foreground">{t('ui.text.open_the_gitfit_app_to_continue')}</p>
            </div>
            <Button
              onClick={handleClose}
              size="lg"
              className="w-full"
              variant="outline"
            >{t('ui.text.close_window')}</Button>
          </>
        )}
        {status === 'processing' && (
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">{t('ui.text.processing')}</h1>
            <p className="text-muted-foreground">{t('ui.text.completing_authentication')}</p>
          </div>
        )}
        {status === 'error' && (
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-destructive">{t('common.error')}</h1>
            <p className="text-muted-foreground">
              {errorMessage}
            </p>
            <Button
              onClick={handleClose}
              size="lg"
              className="w-full"
              variant="outline"
            >{t('ui.text.close_window')}</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OAuthRedirect;
